import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { normalizeCatalogProduct } from "../lib/catalogProduct";

const DEFAULT_LOCAL_HANDLE =
  process.env.REACT_APP_CHEEP_LOCAL_MERCHANT_TAG || "@246_cheepsweepz";

/**
 * Live catalog from DHC /api/products with filter params and refresh.
 * Always requests the @246_cheepsweepz showcase tag variants via local_handle
 * so Instagram-synced rows hydrate into the /products grid.
 */
export function useLiveCatalog({
  api,
  destination,
  currency,
  marketplace,
  category,
  search,
  localHandle = DEFAULT_LOCAL_HANDLE,
}) {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [showcasePolicy, setShowcasePolicy] = useState("");
  const [catalogMeta, setCatalogMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const requestId = useRef(0);

  const fetchCatalog = useCallback(
    async (isRefresh = false) => {
      const id = ++requestId.current;
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      try {
        const params = {
          destination,
          currency,
          limit: 100,
          _t: Date.now(),
        };
        if (marketplace && marketplace !== "all") params.marketplace = marketplace;
        if (category && category !== "all") params.category = category;
        if (search?.trim()) params.search = search.trim();

        const [productsRes, merchantRes] = await Promise.all([
          axios.get(`${api}/products`, {
            params,
            headers: { "Cache-Control": "no-cache" },
            timeout: 25000,
          }),
          axios
            .get(`${api}/catalog/local-merchant`, {
              params: {
                tag: localHandle,
                destination,
                limit: 48,
                _t: Date.now(),
              },
              headers: { "Cache-Control": "no-cache" },
              timeout: 25000,
            })
            .catch(() => null),
        ]);

        if (id !== requestId.current) return;

        const data = productsRes?.data;
        const rows = Array.isArray(data?.products) ? data.products : [];
        const merchantItems = Array.isArray(merchantRes?.data?.items)
          ? merchantRes.data.items
          : [];

        const byId = new Map();
        for (const p of rows) {
          const normalized = normalizeCatalogProduct(p, { destination, currency });
          byId.set(normalized.product_id || normalized.product_sku, normalized);
        }

        for (const item of merchantItems) {
          const productId =
            item.cheep_product_id ||
            item.product_id ||
            `proxy-${item.origin_marketplace || item.marketplace}-${item.itemId || item.origin_item_id}`;
          if (byId.has(productId)) continue;
          const images = Array.isArray(item.images)
            ? item.images
            : Array.isArray(item.image_gallery_urls)
              ? item.image_gallery_urls
              : [];
          byId.set(
            productId,
            normalizeCatalogProduct(
              {
                product_id: productId,
                name: item.title || item.name || "Imported Item",
                marketplace: item.origin_marketplace || item.marketplace || "",
                category: item.category_slug || item.category || "General",
                images,
                real_images: images,
                landed_price: Number(item.list_price || item.source_raw_price || 0) || 0,
                local_merchant_tag: item.local_merchant_tag || localHandle,
                is_featured_locally: true,
                is_verified_image: images.length > 0,
              },
              { destination, currency }
            )
          );
        }

        const merged = Array.from(byId.values());
        setProducts(merged);
        setTotal(Number(data?.total) || merged.length);
        setCategories(Array.isArray(data?.categories) ? data.categories : []);
        setShowcasePolicy(data?.showcase_policy || "");
        setCatalogMeta({
          revision: data?.catalog_revision,
          updatedAt: data?.catalog_updated_at,
          inSync: data?.catalog_in_sync,
          localHandle,
          featuredMerged: merchantItems.length,
        });
      } catch (err) {
        if (id !== requestId.current) return;
        const message =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load products";
        setError(new Error(message));
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [api, destination, currency, marketplace, category, search, localHandle]
  );

  useEffect(() => {
    fetchCatalog(false);
  }, [fetchCatalog]);

  const refresh = useCallback(() => fetchCatalog(true), [fetchCatalog]);

  return {
    products,
    total,
    categories,
    showcasePolicy,
    catalogMeta,
    loading,
    refreshing,
    error,
    refresh,
  };
}
